/**
 * ConfirmDialog
 *
 * Flexible confirmation modal used across the admin panel.
 *
 * Accepts both naming conventions found across the codebase so every
 * call site works without needing individual fixes:
 *   message | description   — body text
 *   danger  | destructive   — red confirm button
 *   confirmLabel            — custom button label (default "Confirm")
 *   loading                 — disables buttons, shows spinner on confirm
 */
export function ConfirmDialog({
  open,
  title,
  message,
  description,
  onConfirm,
  onCancel,
  danger = false,
  destructive = false,
  confirmLabel = 'Confirm',
  loading = false,
}) {
  if (!open) return null;

  const isDestructive = danger || destructive;
  const bodyText = message ?? description ?? '';

  return (
    <>
      <div className="fixed inset-0 bg-black/40 z-50" onClick={loading ? undefined : onCancel} />
      <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
        <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
          <h3 className="text-lg font-semibold text-gray-900 mb-2">{title}</h3>
          {bodyText && (
            <p className="text-sm text-gray-500 mb-6">{bodyText}</p>
          )}
          <div className="flex gap-3 justify-end">
            <button
              onClick={onCancel}
              disabled={loading}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={loading}
              className={`flex items-center gap-2 px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors disabled:opacity-60 disabled:cursor-not-allowed ${
                isDestructive ? 'bg-red-600 hover:bg-red-700' : 'bg-blue-600 hover:bg-blue-700'
              }`}
            >
              {loading && (
                <svg className="w-3.5 h-3.5 animate-spin" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8v4l3-3-3-3v4a8 8 0 00-8 8h4z" />
                </svg>
              )}
              {confirmLabel}
            </button>
          </div>
        </div>
      </div>
    </>
  );
}